import { autocomplete } from '@algolia/autocomplete-js'
import { render } from 'preact'
import '@algolia/autocomplete-theme-classic'
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches'
import Fuse from 'fuse.js'
import hotkeys from 'hotkeys-js'
import groupBy from 'lodash/groupBy'
import { AppContext } from '@/js/components/app-context'
import Loading from '@/js/components/autocomplete/loading'
import NoResults from '@/js/components/autocomplete/no-results'
import PreviewPanel from '@/js/components/autocomplete/preview-panel'
import ProductItem from '@/js/components/autocomplete/product-item'
import ResultsLayout from '@/js/components/autocomplete/results-layout'
import SourceHeader from '@/js/components/autocomplete/source-header'
import addHighlightedAttribute from '@/js/components/autocomplete/utils'

export default function init({
    app,
    autocompleteSelector = '#autocomplete',
    container = document,
    searchPlaceholder = 'Global search',
    inputPlaceholder = 'Searching anything in your MMS data...',
    forceGlobalSearchUrl = null,
    forceQuickActions = null,
    forceQuickActionsProject = null,
    enableHotkeys = true,
}) {
    const $autocomplete = $(autocompleteSelector, container)
    if ($autocomplete.length === 0 || !$autocomplete.is(':visible')) {
        return
    }

    const fuseQuickActionsProject = new Fuse(forceQuickActionsProject ?? app.get('quickActionsProject'), {
        includeScore: true,
        keys: ['label', 'synonyms'],
    })

    const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
        key: `${autocompleteSelector}-v2`,

        transformSource({ source, onRemove }) {
            return {
                ...source,
                getItems(params) {
                    return source.getItems(params).map((item) => addHighlightedAttribute({ item, query: params.query }))
                },
                getItemUrl({ item }) {
                    return item.url
                },
                onSelect({ event, state }) {
                    if (state.status === 'loading') {
                        event.preventDefault()
                    }
                },
                templates: {
                    ...source.templates,
                    header() {
                        return <SourceHeader title="Your recent searches" />
                    },
                    item({ item, components }) {
                        return <ProductItem item={item} components={components} onRemove={onRemove} />
                    },
                },
            }
        },
    })

    const { addItem: addRecentSearchItem } = recentSearchesPlugin.data

    const quickActionsPlugin = {
        getSources() {
            return [
                {
                    sourceId: 'quick-actions',
                    getItemUrl({ item }) {
                        return item.url
                    },
                    getItems({ query }) {
                        return (forceQuickActions ?? app.get('quickActions'))
                            .map((item) => {
                                item.shortDescription = item.category

                                return item
                            })
                            .filter(({ label }) => label.toLowerCase().includes(query.toLowerCase()))
                            .map((item) => addHighlightedAttribute({ item, query }))
                    },
                    onSelect({ event, state }) {
                        if (state.status === 'loading') {
                            event.preventDefault()
                        }
                    },
                    onActive({ setContext }) {
                        setContext({ preview: undefined })
                    },
                    templates: {
                        header() {
                            return <SourceHeader title="Quick actions" />
                        },
                        item({ item, components }) {
                            return <ProductItem item={item} components={components} />
                        },
                    },
                },
            ]
        },
    }

    const quickActionsProjectPlugin = {
        getSources({ query }) {
            if (!query) {
                return []
            }
            return [
                {
                    sourceId: 'quick-actions-project',
                    getItemUrl({ item }) {
                        return item.url
                    },
                    getItems({ query }) {
                        return fuseQuickActionsProject
                            .search(query)
                            .map(({ item }) => {
                                item.shortDescription = item.category

                                return item
                            })
                            .map((item) => addHighlightedAttribute({ item, query }))
                    },
                    onSelect({ event, state }) {
                        if (state.status === 'loading') {
                            event.preventDefault()
                        }
                    },
                    onActive({ setContext }) {
                        setContext({ preview: undefined })
                    },
                    templates: {
                        header() {
                            return <SourceHeader title="Quick actions project" />
                        },
                        item({ item, components }) {
                            return <ProductItem item={item} components={components} />
                        },
                    },
                },
            ]
        },
    }

    const { destroy } = autocomplete({
        container: $autocomplete[0],
        placeholder: inputPlaceholder,
        debug: app.isDev(),
        openOnFocus: true,
        defaultActiveItemId: 0,
        detachedMediaQuery: '',
        plugins: [recentSearchesPlugin, quickActionsPlugin, quickActionsProjectPlugin],
        render({ children, state, components, refresh }, root) {
            const { preview } = state.context
            render(
                <AppContext.Provider value={app}>
                    <ResultsLayout preview={preview} PreviewPanel={PreviewPanel} previewProps={{ components, refresh }}>
                        {children}
                    </ResultsLayout>
                </AppContext.Provider>,
                root
            )
        },
        renderNoResults({ state }, root) {
            if (state.status === 'loading') {
                return render(<Loading />, root)
            }

            if (!state.query || state.status !== 'idle') {
                return
            }

            return render(<NoResults query={state.query} />, root)
        },
        getSources({ query }) {
            if (!query) {
                return []
            }

            const request = $.get(forceGlobalSearchUrl ?? app.get('globalSearchUrl'), { query }, null, 'json')
            const results = new Promise((resolve) => {
                request.then((results) => {
                    resolve(results)
                })
            })

            return results.then((results) => {
                const sources = []
                const groupedResults = groupBy(results, 'type')
                for (const [type, results] of Object.entries(groupedResults)) {
                    const firstResult = results[0]
                    sources.push({
                        sourceId: type,
                        getItemUrl({ item }) {
                            return item.url
                        },
                        getItems() {
                            return results
                                .map((item) => {
                                    if (item.data?.requestNumber) {
                                        item.shortDescription = String(item.data.requestNumber)
                                    }

                                    return item
                                })
                                .map((item) => addHighlightedAttribute({ item, query }))
                        },
                        onSelect({ item }) {
                            addRecentSearchItem(item)
                        },
                        onActive({ item, setContext }) {
                            setContext({ preview: item })
                        },
                        templates: {
                            header() {
                                return <SourceHeader title={firstResult.category} />
                            },
                            item({ item, components }) {
                                return <ProductItem item={item} components={components} />
                            },
                        },
                    })
                }

                return sources
            })
        },
    })

    const isMacOS = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform) || navigator.appVersion.indexOf('Mac') !== -1
    const $autocompleteBtn = $autocomplete.find('.aa-Autocomplete button')

    $autocompleteBtn.find('.aa-DetachedSearchButtonPlaceholder').text(searchPlaceholder)

    if (enableHotkeys) {
        const kbd = $('<kbd>')
        if (isMacOS) {
            kbd.text(`Cmd + K`)
        } else {
            kbd.text(`Ctrl + K`)
        }
        kbd.appendTo($autocompleteBtn)

        hotkeys('ctrl+k,cmd+k', (event) => {
            event.preventDefault()
            $autocompleteBtn.click()
        })
    }

    return function cleanup() {
        if (enableHotkeys) {
            hotkeys.unbind('ctrl+k,cmd+k')
        }
        destroy()
    }
}
